import { createSelector } from '@reduxjs/toolkit';

import {
  DentalNotationFormat,
  FeatureFlag,
} from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { OrganizationStaffRoleType } from '@/shared/api/protocol-ts/model/dto_organization_staff_pb';

import { organizationStaffSelectors } from '@/entities/organizationStaff/model/organizationStaffSlice.selectors';

import { RootState } from '@/app/model/store';

import { TREATING_DOCTORS_AVAILABLE_ROLES } from '../config';
import { OrganizationMember } from '../config/types';
import {
  deriveFDAEnvironment,
  FDAEnvironment,
  getOrganizationAvatarData,
  OrganizationAvatarData,
} from '../lib';

import { organizationAdapter } from './organizationSlice';

export const organizationSelectors = organizationAdapter.getSelectors(
  (state: RootState) => state.organization,
);

export const selectLoading = (state: RootState) => state.organization.loading;

export const { selectIds } = organizationSelectors;

export const selectCurrentOrganizationID = (state: RootState) =>
  state.organization.currentOrganizationID;

export const selectSettingsLoadedForOrganizationID = (state: RootState) =>
  state.organization.settingsLoadedForOrganizationID;

export const selectCurrentOrganizationSettingsLoaded = createSelector(
  [selectCurrentOrganizationID, selectSettingsLoadedForOrganizationID],
  (currentOrganizationID, settingsLoadedForOrganizationID) =>
    Boolean(currentOrganizationID) &&
    settingsLoadedForOrganizationID === currentOrganizationID,
);

export const selectCurrentOrganization = createSelector(
  [organizationSelectors.selectEntities, selectCurrentOrganizationID],
  (organizationEntities, organizationID) =>
    organizationEntities[organizationID],
);

// NOTE: patientOrganization is needed for cases when the user is not a member of the organization associated with the patient (admin or shared doctor)
export const selectPatientOrganization = (state: RootState) =>
  state.organization.patientOrganization;

// NOTE: patientOrganization comes from the patient profile stream with its feature flags,
// so its presence means the settings are loaded even for a viewer without an own organization
export const selectRelevantOrganizationSettingsLoaded = createSelector(
  [selectPatientOrganization, selectCurrentOrganizationSettingsLoaded],
  (patientOrganization, isCurrentOrganizationSettingsLoaded) =>
    Boolean(patientOrganization) || isCurrentOrganizationSettingsLoaded,
);

export const selectCurrentOrganizationStaff = createSelector(
  [organizationStaffSelectors.selectAll, selectCurrentOrganizationID],
  (allStaff, organizationID) =>
    allStaff.filter((staff) => staff.OrganizationID === organizationID),
);

export const selectPatientOrganizationStaff = createSelector(
  [organizationStaffSelectors.selectAll, selectPatientOrganization],
  (allStaff, patientOrganization) =>
    allStaff.filter(
      (staff) => staff.OrganizationID === patientOrganization?.ID,
    ),
);

// NOTE: If patientProfileStream contains OrganizationStaff for the patient's organization, we use it.
// Otherwise, we use staff from the organizationStream. This is needed for cases when the user is not part of the organization linked to the patient (e.g. admin or a shared doctor)
export const selectAllUsers = createSelector(
  selectPatientOrganization,
  selectPatientOrganizationStaff,
  selectCurrentOrganizationStaff,
  (
    patientOrganization,
    patientOrganizationStaff,
    currentOrganizationStaff,
  ): OrganizationMember[] =>
    patientOrganization && patientOrganizationStaff.length > 0
      ? patientOrganizationStaff
      : currentOrganizationStaff,
);

export const selectUserByID = (userID: string) =>
  createSelector(selectAllUsers, (allUsers) =>
    allUsers.find((user) => user.UserID === userID),
  );

export const selectStaffByID = (staffID: string) =>
  createSelector(selectAllUsers, (allUsers) =>
    allUsers.find((staff) => staff.ID === staffID),
  );

export const selectAllDoctors = createSelector(selectAllUsers, (allUsers) =>
  allUsers?.filter(
    (user) =>
      TREATING_DOCTORS_AVAILABLE_ROLES.includes(user.Role) ||
      user.Role ===
        OrganizationStaffRoleType.OrganizationStaffRoleType_NonClinicalMember,
  ),
);

export const selectTreatingDoctors = createSelector(
  selectAllUsers,
  (allUsers) =>
    allUsers?.filter(
      (user) =>
        TREATING_DOCTORS_AVAILABLE_ROLES.includes(user.Role) &&
        user.Status.case === 'Active',
    ),
);

export const selectAllTreatingDoctors = createSelector(
  selectAllUsers,
  (allUsers) =>
    allUsers?.filter((user) =>
      TREATING_DOCTORS_AVAILABLE_ROLES.includes(user.Role),
    ),
);

export const selectDentalNotationFormat = createSelector(
  selectCurrentOrganization,
  (organization) =>
    organization?.Settings?.DentalNotationFormat ||
    DentalNotationFormat.DentalNotationFormatISO,
);

export const selectOrganizationLanguage = createSelector(
  selectCurrentOrganization,
  (organization) => organization?.Settings?.Language ?? '',
);

const normalizeFeatureFlag = (flag: FeatureFlag | keyof typeof FeatureFlag) =>
  typeof flag === 'string' ? FeatureFlag[flag] : flag;

export const selectFeatureList = createSelector(
  selectCurrentOrganization,
  (organization) =>
    (organization?.FeatureFlags ?? []).reduce(
      (features, { Flag, Value }) => ({
        ...features,
        [normalizeFeatureFlag(Flag)]: Value,
      }),
      {} as Record<FeatureFlag, boolean>,
    ),
);

export const selectIsMFASettingsEnabled = createSelector(
  selectFeatureList,
  (features) => Boolean(features[FeatureFlag.FeatureFlag_Enable_MFASettings]),
);

export const selectPatientOrganizationFeatureList = createSelector(
  selectPatientOrganization,
  (patientOrganization) =>
    (patientOrganization?.FeatureFlags ?? []).reduce(
      (features, { Flag, Value }) => ({
        ...features,
        [normalizeFeatureFlag(Flag)]: Value,
      }),
      {} as Record<FeatureFlag, boolean>,
    ),
);

export const selectRelevantFeatureList = createSelector(
  selectPatientOrganization,
  selectPatientOrganizationFeatureList,
  selectFeatureList,
  (
    patientOrganization,
    patientOrganizationFeatures,
    currentOrganizationFeatures,
  ) =>
    patientOrganization
      ? patientOrganizationFeatures
      : currentOrganizationFeatures,
);

export const selectFeatureFlag = (featureName: FeatureFlag) =>
  createSelector(
    selectRelevantFeatureList,
    (features) => features[featureName],
  );

// Single source of truth for the FDA clinical-environment mode, derived from the
// four FDA feature flags. FDA Version2 can enable the FDA baseline by itself;
// legacy FDA orgs (Version2 off) behave exactly as before — see deriveFDAEnvironment.
// While organization settings are loading (after clinic switch), returns a "safe"
// state with masksVisible: false to prevent showing masks with stale flags.
export const selectFDAEnvironment = createSelector(
  selectRelevantOrganizationSettingsLoaded,
  selectFeatureFlag(FeatureFlag.FeatureFlag_FDA_3DGPRestrictions),
  selectFeatureFlag(FeatureFlag.FeatureFlag_FDA_Version2),
  selectFeatureFlag(FeatureFlag.FeatureFlag_FDA_Annotator),
  selectFeatureFlag(FeatureFlag.FeatureFlag_FDA_Unaided),
  (
    isSettingsLoaded,
    isFDA3DGPRestrictions,
    isFDAVersion2,
    isFDAAnnotator,
    isFDAUnaided,
  ) => {
    // Return safe state while organization settings are loading to prevent
    // showing masks with stale FDA flags during clinic switch.
    // Explicitly set masksVisible: false without side effects on other flags.
    if (!isSettingsLoaded) {
      const loadingState: FDAEnvironment = {
        mode: 'none',
        isFDA: false,
        isUnaided: false,
        isFDAVersion2: false,
        isFDA3DGPRestrictions: false,
        predictsVisible: true,
        masksVisible: false, // hide masks until settings are loaded
        manualPercentInput: false,
        periapicalConditionVisible: false,
        confidenceSliderVisible: false,
        treatToothLikeAsTooth: false,
      };

      return loadingState;
    }

    return deriveFDAEnvironment({
      isFDA3DGPRestrictions,
      isFDAVersion2,
      isFDAAnnotator,
      isFDAUnaided,
    });
  },
);

export const selectOrganizationStampAssetID = createSelector(
  selectCurrentOrganization,
  selectPatientOrganization,
  (currentOrganization, patientOrganization) => {
    return patientOrganization
      ? patientOrganization.StampAssetID
      : currentOrganization?.StampAssetID;
  },
);

export const selectCurrentOrganizationAvatarData = createSelector(
  selectCurrentOrganization,
  (organization) =>
    organization
      ? getOrganizationAvatarData(organization)
      : ({} as OrganizationAvatarData),
);

export const selectOtherOrganizationsAvatarData = createSelector(
  organizationSelectors.selectAll,
  selectCurrentOrganizationID,
  (organizations, currentOrganizationID) =>
    organizations
      .filter((organization) => organization.ID !== currentOrganizationID)
      .map(getOrganizationAvatarData),
);

export const selectIsUserOwner = (userID: string) =>
  createSelector(
    selectUserByID(userID),
    (user) =>
      user?.Role ===
      OrganizationStaffRoleType.OrganizationStaffRoleType_FullAccess,
  );

export const selectStorageUsed = createSelector(
  (state: RootState) => state.organization.storageUsed,
  (storageUsed) => BigInt(storageUsed),
);

export const selectOrganizationsRequiringMFA = createSelector(
  [organizationSelectors.selectAll],
  (organizations) =>
    organizations.filter((organization) => organization.MFARequiredForActor),
);
